import { ChangeEvent, useState } from 'react';

import { updateProcesso, type Processo } from '@/api/client';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { useNotification } from '@/context/NotificationProvider';
import { useOfflineMutation } from '@/hooks/useOfflineMutation';
import { getUserVisibleErrorMessage } from '@/lib/errors';
import { queryKeys } from '@/lib/pwa/queryConfig';
import { cn } from '@/lib/utils';

interface ProcessoEditDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  processo: Processo | null;
}

export function ProcessoEditDialog({ open, onOpenChange, processo }: ProcessoEditDialogProps) {
  // Use key-based remount via the inner form component to avoid setState in effects
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="w-full sm:max-w-lg">
        {processo && (
          <ProcessoEditForm
            key={processo.id}
            processo={processo}
            onOpenChange={onOpenChange}
          />
        )}
      </DialogContent>
    </Dialog>
  );
}

// --- Inner form component (remounted via key when processo changes) ---

interface ProcessoEditFormProps {
  processo: Processo;
  onOpenChange: (open: boolean) => void;
}

function ProcessoEditForm({ processo, onOpenChange }: ProcessoEditFormProps) {
  const { showError, showSuccess } = useNotification();
  const [editForm, setEditForm] = useState({
    tribunal: processo.tribunal,
    classe: processo.classe,
    status: processo.status,
    possui_permissao_peticionar: processo.possui_permissao_peticionar,
  });
  const [editErrors, setEditErrors] = useState<Record<string, string>>({});

  const validateEditForm = () => {
    const errors: Record<string, string> = {};
    if (!editForm.tribunal.trim()) {
      errors.tribunal = 'Tribunal é obrigatório';
    }
    if (!editForm.classe.trim()) {
      errors.classe = 'Classe processual é obrigatória';
    }
    return errors;
  };

  const updateMutation = useOfflineMutation({
    mutationFn: ({ id, payload }: { id: string; payload: Partial<Processo> }) => updateProcesso(id, payload),
    outbox: {
      type: 'processo_editar',
      getPayload: ({ id, payload }) => ({
        method: 'PUT',
        url: `/processos/${id}`,
        body: payload,
      }),
      getDescription: ({ id }) => `Editar processo ${id}`,
    },
    invalidateKeys: [[...queryKeys.processos.all]],
    onSuccess: () => {
      showSuccess('Processo atualizado com sucesso');
      onOpenChange(false);
    },
    onQueued: () => {
      showSuccess('Processo atualizado com sucesso (será sincronizado quando houver conexão)');
      onOpenChange(false);
    },
  });

  const handleEditChange = (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const name = event.target.name as string;
    const value = event.target.value;
    setEditForm((prev) => ({ ...prev, [name]: value }));
    if (editErrors[name]) {
      setEditErrors((prev) => {
        const next = { ...prev };
        delete next[name];
        return next;
      });
    }
  };

  const handleEditSubmit = async () => {
    const errors = validateEditForm();
    if (Object.keys(errors).length > 0) {
      setEditErrors(errors);
      return;
    }
    setEditErrors({});
    try {
      await updateMutation.mutateAsync({
        id: processo.id,
        payload: {
          tribunal: editForm.tribunal,
          classe: editForm.classe,
          status: editForm.status,
          possui_permissao_peticionar: editForm.possui_permissao_peticionar,
        },
      });
    } catch (error: unknown) {
      const msg = getUserVisibleErrorMessage(error);
      if (msg) showError(msg);
    }
  };

  return (
    <>
      <DialogHeader>
        <DialogTitle>Editar Processo</DialogTitle>
      </DialogHeader>
      <div className="flex flex-col gap-6">
        <div className="flex flex-col gap-2">
          <Label>Número do Processo</Label>
          <Input
            value={processo.numero}
            disabled
          />
        </div>
        <div className="flex flex-col gap-2">
          <Label>Tribunal *</Label>
          <Input
            name="tribunal"
            value={editForm.tribunal}
            onChange={handleEditChange}
            className={cn(editErrors.tribunal && 'border-destructive')}
          />
          {editErrors.tribunal && (
            <span className="text-xs text-destructive">{editErrors.tribunal}</span>
          )}
        </div>
        <div className="flex flex-col gap-2">
          <Label>Classe Processual *</Label>
          <Input
            name="classe"
            value={editForm.classe}
            onChange={handleEditChange}
            className={cn(editErrors.classe && 'border-destructive')}
          />
          {editErrors.classe && (
            <span className="text-xs text-destructive">{editErrors.classe}</span>
          )}
        </div>
        <div className="flex flex-col gap-2">
          <Label>Status</Label>
          <Select
            value={editForm.status}
            onValueChange={(value) => setEditForm((prev) => ({ ...prev, status: value }))}
          >
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="ATIVO">Ativo</SelectItem>
              <SelectItem value="ENCERRADO">Encerrado</SelectItem>
              <SelectItem value="SUSPENSO">Suspenso</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="flex items-center gap-3">
          <Switch
            checked={editForm.possui_permissao_peticionar}
            onCheckedChange={(checked) => setEditForm((prev) => ({ ...prev, possui_permissao_peticionar: checked }))}
            disabled={processo.tipo !== 'PATROCINIO'}
          />
          <Label>Permissão para peticionar</Label>
        </div>
      </div>
      <DialogFooter>
        <Button variant="ghost" onClick={() => onOpenChange(false)}>
          Cancelar
        </Button>
        <Button onClick={handleEditSubmit} disabled={updateMutation.isPending || !editForm.tribunal.trim() || !editForm.classe.trim()}>
          {updateMutation.isPending ? 'Salvando...' : 'Salvar alterações'}
        </Button>
      </DialogFooter>
    </>
  );
}
